import { StatusCode } from "@/modules/types/statusCode";
import { isClient } from "./client";
import { logError } from "@/lib/logger/logger";

export class AppError extends Error {
  level: "ERROR" | "WARN" | "INFO" | "DEBUG" = "ERROR";
  action: string = "";
  ApiAction: string = "";
  code: number = 0;
  message: string = "";
  reason: string = "";
  details: ErrorDetail[] = [];
  private apiBaseUrl = process.env.BASE_API_URL || "http://localhost:3000/api";
  private apiKey = process.env.API_KEY || "1234567890";

  constructor(error: any) {
    const message = "Unknown error";
    super(message);
    this.name = "AppError";
    if (error instanceof AppError) {
      this.code = error.code;
      this.message = error.message;
      this.reason = error.reason;
      this.details = error.details || [];
      if (error.stack) {
        this.stack = error.stack;
      }
      this.action = error.action;
      this.ApiAction = error.ApiAction;
      this.level = error.level;
    } else if (typeof error === "object" && error?.response?.data) {
      const err: AppError = error.response.data;
      this.message = err.message;
      this.code = err.code;
      this.reason = err.reason;
      this.details = err.details || [];
    } else if (error instanceof Error) {
      // Must be checked before generic `typeof object` — TypeError/fetch errors land here
      this.message = error.message;
      if (error.stack) {
        this.stack = error.stack;
      }
      const cause = (error as any).cause;
      if (cause) {
        const causeMsg =
          typeof cause === "object"
            ? cause.message || cause.code || JSON.stringify(cause)
            : String(cause);
        this.reason = causeMsg;
        this.details = [
          {
            "@type": "ErrorCause",
            metadata: new Map(),
            reason: causeMsg,
            ...(typeof cause === "object" ? cause : {}),
          },
        ];
      } else {
        this.details = [];
      }
    } else if (typeof error === "object" && error !== null) {
      const appError = error as AppError;
      this.code = appError.code || 0;
      this.message = appError.message || "";
      this.reason = appError.reason || "";
      this.details = appError.details || [];
      if ((error as any).stack) {
        this.stack = (error as any).stack;
      }
    }
  }

  setAction(action: string) {
    this.action = action;
  }

  setApiAction(apiAction: string) {
    this.ApiAction = apiAction;
  }

  logError(caller?: string) {
    const obj = {
      caller,
      name: this.name,
      code: this.code,
      message: this.message,
      reason: this.reason,
      details: this.details,
      stack: this.stack,
      action: this.action,
      apiAction: this.ApiAction,
    };

    if (isClient()) {
      if (process.env.NODE_ENV && process.env.NODE_ENV !== "production") {
        // Use Winston logger instead of console.error
        logError("Client-side error", {
          ...obj,
          environment: process.env.NODE_ENV,
          timestamp: new Date().toISOString(),
        });
      }

      // Fire-and-forget — must have .catch() to avoid Unhandled Promise Rejection
      // crashing the Node.js process when the log endpoint is unreachable
      fetch(`${this.apiBaseUrl}/log/error`, {
        method: "POST",
        body: JSON.stringify(obj),
        headers: {
          "Content-Type": "application/json",
          "x-api-key": this.apiKey,
        },
      }).catch(() => {
        // Intentionally swallowed: log endpoint failure must not crash the app
      });
    } else {
      // Use Winston logger instead of console.error
      logError("Server-side error", {
        ...obj,
        environment: process.env.NODE_ENV,
        timestamp: new Date().toISOString(),
      });
    }
  }

  isAuthError() {
    return this.code == StatusCode.UNAUTHENTICATED;
  }
}

export type ErrorDetail = {
  "@type": string;
  metadata: Map<string, string>;
  reason: string;
  [key: string]: any;
};
